/**
 * Cookie access for preference resolution. Deliberately free of `js-cookie` and
 * `next/headers` so it can be imported from a server component or the client alike —
 * the caller supplies the source, this module normalises the shape.
 */

import {
  affiliateCookieName,
  currencyCookieName,
  destinationCookieName,
  originCookieName,
} from '../constants/http'
import {CookieRecord, CookieStore} from './types'

/**
 * The only cookies allowed to cross into a client component.
 *
 * Props handed to a `'use client'` component are serialised into the RSC payload,
 * which is embedded in the HTML document. Passing the whole cookie store therefore
 * publishes every cookie — including `next-auth.session-token`, which is httpOnly
 * precisely so that page scripts cannot read it. Keep this list minimal: add a name
 * only once something on the client actually needs it.
 */
export const clientCookieNames: readonly string[] = [
  affiliateCookieName,
  currencyCookieName,
  destinationCookieName,
  originCookieName,
]

/**
 * Normalises a Next.js cookie store (`await cookies()` in a server component) into the
 * plain record `resolvePreferences` expects, keeping only the names in `clientCookieNames`.
 */
export const toClientCookieRecord = (store: CookieStore): CookieRecord =>
  Object.fromEntries(
    store
      .getAll()
      .filter(({name}) => clientCookieNames.includes(name))
      .map(({name, value}) => [name, value]),
  )

/**
 * The same record, read from `document.cookie` in the browser. A statically rendered page has no
 * request to read cookies from, so the client is the only place its preferences can come from.
 */
export const readClientCookieRecord = (): CookieRecord =>
  Object.fromEntries(
    document.cookie
      .split('; ')
      .map((entry) => {
        const separator = entry.indexOf('=')
        return separator === -1
          ? ['', '']
          : [entry.slice(0, separator), decodeURIComponent(entry.slice(separator + 1))]
      })
      .filter(([name]) => clientCookieNames.includes(name)),
  )
